import { MathsKit, MathsKitOptions, MarkLine } from '../mathsKit';
import { MathsKitManager } from '../manager';
import { Point2d } from '../utils/Point2d';
import Matrix2D, { createRotationMatrix, composeTransforms, decomposeMatrix } from '../utils/matrix';
import { LineSegment, Vec2d } from '../utils';
import { MathsKitElement, MathsKitType } from '../types';
import { MM_PER_PIXELS, PIXELS_PER_MM } from '../utils/const';
import { throttle } from 'lodash';

export type Triangle30Element = HTMLDivElement;
export type Triangle45Element = HTMLDivElement;

export class Triangle30Tool extends MathsKit {
  protected markLines: MarkLine[] = [];
  private deleteBtn: HTMLDivElement = document.createElement('div');
  private rotateBtn: HTMLDivElement = document.createElement('div');
  private angleInput: HTMLInputElement = document.createElement('input');
  private lineLength: HTMLDivElement = document.createElement('div');
  private stretchBtn!: HTMLDivElement;
  private triangleView!: HTMLDivElement;
  private elementContent!: HTMLDivElement;
  private bottomMark!: HTMLDivElement;
  private leftMark!: HTMLDivElement;
  private hypotenuseMark!: HTMLDivElement;

  bindElement(element: MathsKitElement, type: MathsKitType) {
    this.element.classList.add('maths-kit', `maths-kit-${type}`, `maths-kit-${this.manager.theme}`);
    this.manager.container.appendChild(element);
  }

  private bindElementContent(element: MathsKitElement){
    this.elementContent = document.createElement('div');
    this.elementContent.classList.add('maths-kit-content');
    element.appendChild(this.elementContent);
  }

  bindTriangleView(element: MathsKitElement){
    const triangleView = document.createElement('div');
    triangleView.classList.add('maths-kit-view');
    triangleView.setAttribute('data-type', 'Drag');
    triangleView.setAttribute('data-id', this.id);
    element.appendChild(triangleView);
    return triangleView;
  }
  
  constructor(options: MathsKitOptions, manager: MathsKitManager) {
    super(options, manager);
    this.bindElementContent(this.element);
  }

  init(): void {
    this.triangleView = this.bindTriangleView(this.elementContent);
    this.bottomMark = this.createMark(this.elementContent, 'bottom');
    this.leftMark = this.createMark(this.elementContent, 'left');
    this.hypotenuseMark = this.createMark(this.triangleView, 'top');
    this.createDeleteBtn();
    this.createRotateBtn();
    this.createStretchBtn();
    this.createAngleInput();
    this.createLineLength();
    this.bindEvent();
    this.render();
  }

  onDragStart(): void {
    this.toTop();
  }

  onDragDoing(): void {}

  onDragEnd(matrix: Matrix2D): void {
    this.updateStateMatrix(matrix);
  }

  onRotateStart(originAngle: number): void {
    this.toTop();
    this.updateAngleDisplay(originAngle);
  }

  onRotateDoing(angle: number): void {
    this.updateAngleDisplay(angle);
  }

  onRotateEnd(matrix: Matrix2D): void {
    this.updateStateMatrix(matrix);
  }

  onStretchStart(): void {
    this.toTop();
    this.thottled.cancel();
  }

  private thottled = throttle((_width: number): void => {
    this.element.style.width = `${_width}px`;
    this.elementContent.style.width = `${_width}px`;
    this.elementContent.style.height = `${this.computeHeight(_width)}px`;
    this.triangleView.style.width = `${this.computeHypotenuse(_width)}px`;
    this.renderMark(this.elementContent);
  },50);

  onStretchDoing = (_width: number): void => {
    this.thottled(_width);    
  };

  onStretchEnd(width: number): void {
    this.thottled.cancel();
    // 更新三角形的宽度
    this.manager.update(this.id, { width: width });
  }

  /**
   * 获取旋转中心点, 坐标必须相对于容器左上角, 旋转中心点是左上角
   * @returns 
   */
  getRotateCenterPoint = () => {
    const { x, y } = this.state;
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const pxX = x * containerWidth;
    const pxY = y * containerHeight;
    const domMatrix = this.getDomMatrix();
    const { translateX, translateY } = decomposeMatrix(domMatrix);
    const leftTopPoint = new Vec2d(pxX + translateX, pxY + translateY);
    // 返回 Point2d 对象
    return new Point2d(leftTopPoint.x, leftTopPoint.y);
  };

  getMarkLines(): MarkLine[] {
    const { x, y, width } = this.state;
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const { translateX, translateY, scaleX, scaleY } = decomposeMatrix(this.getDomMatrix());
    const height = this.computeHeight(width);
    // 30-60-90三角形的三个顶点
    const baseWidth = width * scaleX;
    const baseHeight = height * scaleY;

    const pxX = x * containerWidth;
    const pxY = y * containerHeight;
    
    // 计算三角形的三个顶点（30-60-90三角形）
    // 直角在左下角 (0, height)
    // 底边（x轴邻边）从 (0, height) 到 (width, height)
    // 左边（y轴邻边）从 (0, height) 到 (0, 0)
    // 斜边从 (0, 0) 到 (width, height)
    const leftPoint0 = new Vec2d(pxX , pxY - baseHeight );
    const leftPoint1 = new Vec2d(pxX, pxY);

    const hypotenusePoint0 = new Vec2d(pxX, pxY - baseHeight);
    const hypotenusePoint1 = new Vec2d(pxX + baseWidth, pxY);

    const bottomPoint0 = new Vec2d(pxX, pxY);
    const bottomPoint1 = new Vec2d(pxX + baseWidth, pxY);
    
    // 应用变换
    leftPoint0.add(new Vec2d(translateX, translateY));
    leftPoint1.add(new Vec2d(translateX, translateY));
    hypotenusePoint0.add(new Vec2d(translateX, translateY));
    hypotenusePoint1.add(new Vec2d(translateX, translateY));
    bottomPoint0.add(new Vec2d(translateX, translateY));
    bottomPoint1.add(new Vec2d(translateX, translateY));
    
    const currentAngle = this.getCurrentAngle();
    const angleInRadians = (currentAngle * Math.PI) / 180;
    const rotateCenterPoint = this.getRotateCenterPoint();
    
    leftPoint0.rotWith(rotateCenterPoint, -angleInRadians);
    leftPoint1.rotWith(rotateCenterPoint, -angleInRadians);
    hypotenusePoint0.rotWith(rotateCenterPoint, -angleInRadians);
    hypotenusePoint1.rotWith(rotateCenterPoint, -angleInRadians);
    bottomPoint0.rotWith(rotateCenterPoint, -angleInRadians);
    bottomPoint1.rotWith(rotateCenterPoint, -angleInRadians);

    const leftLine = new LineSegment(leftPoint0, leftPoint1);
    const hypotenuse = new LineSegment(hypotenusePoint0, hypotenusePoint1);
    const baseLine = new LineSegment(bottomPoint0, bottomPoint1);

    const markLines: MarkLine[] = [
      {
        axis: 'y',
        direction: 'left',
        line: leftLine,
      },
      {
        axis: 'x',
        direction: 'topLeftToBottomRight',
        line: hypotenuse,
      },
      {
        axis: 'x',
        direction: 'bottom',
        line: baseLine,
      },
    ];
    return markLines;
  }

  private bindEvent() {
    this.deleteBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.deleteBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    this.deleteBtn.addEventListener('click', this.onDeleteBtnClickHandler);

    this.stretchBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.stretchBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    
    this.rotateBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.rotateBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    
    this.angleInput.addEventListener('focus', this.onAngleInputFocusHandler);
    this.angleInput.addEventListener('blur', this.onAngleInputBlurHandler);
    this.angleInput.addEventListener('keydown', this.onAngleInputKeyDownHandler);
    
    // 添加鼠标悬停显示角度输入框
    this.rotateBtn.addEventListener('mouseenter', this.showAngleInput);
    this.angleInput.addEventListener('mouseenter', this.showAngleInput);
  }

  private unbindEvent() {
    if (this.deleteBtn) {
      this.deleteBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.deleteBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
      this.deleteBtn.removeEventListener('click', this.onDeleteBtnClickHandler);
    }

    if (this.stretchBtn) {
      this.stretchBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.stretchBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
    }
    
    if (this.rotateBtn) {
      this.rotateBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.rotateBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
    }
    
    if (this.angleInput) {
      this.angleInput.removeEventListener('focus', this.onAngleInputFocusHandler);
      this.angleInput.removeEventListener('blur', this.onAngleInputBlurHandler);
      this.angleInput.removeEventListener('keydown', this.onAngleInputKeyDownHandler);
      // 移除鼠标悬停事件
      this.rotateBtn.removeEventListener('pointerenter', this.showAngleInput);
      this.angleInput.removeEventListener('pointerenter', this.showAngleInput);
    }
    this.thottled.cancel();
  }

  destroy(): void {
    this.triangleView.remove();
    this.bottomMark.remove();
    this.leftMark.remove();
    this.hypotenuseMark.remove();
    super.destroy();
    this.unbindEvent();
  }

  private onDeleteBtnClickHandler = (e: MouseEvent) => {
    e.stopPropagation();
    this.manager.remove(this.id);
  };

  private onBtnMouseEnterHandler = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.background = 'var(--maths-kit-math-kit-btn-hover-bg, #f0f4ff)';
    if (e.target === this.stretchBtn) {
      target.style.transform = 'translateY(-50%) scale(1.1) rotate(-90deg)';
    } else {
      target.style.transform = 'translateY(-50%) scale(1.1)';
    }
  };

  private onBtnMouseLeaveHandler = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.background = 'var(--maths-kit-math-kit-btn-bg, #fff)';
    if (e.target === this.stretchBtn) {
      target.style.transform = 'translateY(-50%) scale(1) rotate(-90deg)';
    } else {
      target.style.transform = 'translateY(-50%) scale(1)';
    }
  };

  createElement(): HTMLDivElement {
    const element = document.createElement('div');
    
    // 确保容器有正确的定位上下文
    element.style.position = 'relative';
    element.style.transformOrigin = 'left bottom';
    element.style.touchAction = 'none';

    return element;
  }

  private createMark(element: HTMLDivElement, position: 'bottom' | 'left' | 'top') {
    const mark = document.createElement('div');
    mark.classList.add('maths-kit-triangle-mark', position);
    // 应用内联样式
    Object.assign(mark.style, {
      position: 'absolute',
      left: position === 'left' ? '28px' : '0',
      background: 'transparent',
      ...(position === 'top' ? { 
        top: '0',
        right: '0',
        borderBottom: 'none',
      } : position === 'left' ? { 
        bottom: '0',
        borderRight: 'none',
        transform: 'rotate(-90deg)',
        transformOrigin: 'left bottom',
      } : { 
        bottom: '0',
        right: '0',
        borderTop: 'none',
      }),
      pointerEvents: 'none',
    });
    element.appendChild(mark);
    return mark;
  }

  private computeHeight(width: number) {
    return width * Math.sqrt(3) / 3;
  }

  private computeHypotenuse(width: number) {
    return 2 * this.computeHeight(width);
  }

  render(): void {
    const state = this.state;
    const domMatrix = this.getDomMatrix();
    const height = this.computeHeight(state.width);
    const width = state.width;

    const style = {
      position: 'absolute',
      left: `${state.x * 100}%`,
      top: `${state.y * 100}%`,
      height: '1px',
      width: `${width}px`,
      minWidth: '200px',
      transform: domMatrix.toCSSTransform(),
      zIndex: state.zIndex,
      boxSizing: 'content-box',
    };
    Object.assign(this.element.style, style);

    const elementContentStyle = {
      position: 'absolute',
      bottom: '0',
      left: '0',
      width: `${width}px`,
      height: `${height}px`,
      minWidth: '200px',
      minHeight: `${this.computeHeight(200)}px`,
      overflow: 'hidden',
      boxSizing: 'content-box',
      borderLeft: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      borderBottom: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      borderRight: '0',
      borderTop: '0',
      pointerEvents: 'none',
    };
    Object.assign(this.elementContent.style, elementContentStyle);

    const angleInRadians = (330 * Math.PI) / 180;
    const triangleMatrix = createRotationMatrix(angleInRadians);
    const hypotenuse = this.computeHypotenuse(state.width);
    const hypotenuseHeight = this.computeHeight(hypotenuse);
    const minHypotenuseWidth = this.computeHypotenuse(200);
    const minHypotenuseHeight = this.computeHeight(hypotenuse);
    const triangleViewStyle = {
      position: 'absolute',
      left: '0',
      top: '0',
      width: `${hypotenuse}px`,
      height: `${hypotenuseHeight}px`,
      minWidth: `${minHypotenuseWidth}px`,
      minHeight: `${minHypotenuseHeight}px`,
      transform: triangleMatrix.toCSSTransform(),
      boxSizing: 'border-box',
      transformOrigin: 'left top',
      borderTop: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      backgroundColor: 'var(--maths-kit-ruler-bg, rgba(255, 255, 255, 0.8))',
      // border: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      pointerEvents: this._isActive ? 'auto' : 'none',
      cursor: 'pointer',
    };
    Object.assign(this.triangleView.style, triangleViewStyle);
    
    // 使用setTimeout确保DOM完全渲染后再获取尺寸和重新绑定事件
    setTimeout(() => {
      this.renderMark(this.elementContent);
    }, 0);

  }
  private renderMark(element: Triangle30Element) {
    const marks = element.querySelectorAll('.maths-kit-triangle-mark') as NodeListOf<HTMLDivElement>;
    marks.forEach((mark) => {
      this.renderMarkAtPosition(mark);
    });
  }

  private renderMarkAtPosition(mark: HTMLDivElement) {
    const position = mark.classList.contains('top') ? 'top' : mark.classList.contains('bottom') ? 'bottom' : 'left';
    // 清空现有内容
    mark.innerHTML = '';

    let svgWidth = 0;
    // SVG内部padding
    if(position === 'left') {
      const width = this.elementContent.offsetHeight;
      if (width <= 0) return;
      svgWidth = width;
    } else if (position === 'bottom') {
      const width = this.elementContent.offsetWidth;
      if (width <= 0) return;
      svgWidth = width;
    } else {
      const width = mark.offsetWidth;
      if (width <= 0) return;
      svgWidth = width;
    }
    let SVG_PADDING = 40;
    if (position === 'top') {
      SVG_PADDING = 55;
    }

    const SVG_BG_HEIGHT = 28;

    // 保持原有的绝对定位，只更新必要的样式
    if (position === 'left') {
      mark.style.width = `${svgWidth}px`; // 减少高度，因为刻度线变短了
      mark.style.height = `${SVG_BG_HEIGHT}px`;
    } else {
      mark.style.height = `${SVG_BG_HEIGHT}px`; // 减少高度，因为刻度线变短了
    }
    mark.style.overflow = 'hidden';
    mark.style.zIndex = '1';

    // 获取设备像素比，用于更精确的物理尺寸计算
    // const devicePixelRatio = window.devicePixelRatio || 1;
    const MM_PER_CM = 10;
    
    // 刻度间距：1mm
    const MARK_SPACING_MM = 1;
    const MARK_SPACING_PX = MARK_SPACING_MM * PIXELS_PER_MM;
    
    // 刻度线宽度：1px
    const MARK_WIDTH_PX = 1;
    
    // 计算可以容纳的刻度数量（考虑SVG内部padding）
    let availableWidth = svgWidth;
    if (position === 'bottom') {
      availableWidth = availableWidth - (SVG_PADDING * 2.5);
    } else {
      availableWidth = availableWidth - (SVG_PADDING * 2);
    }
    const totalMarks = Math.floor(availableWidth / MARK_SPACING_PX);
    
    // 创建SVG容器
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const width = position === 'left' ? svgWidth - 10 : position === 'bottom' ? svgWidth - 20 : svgWidth;
    svg.setAttribute('width', `${width}`);
    svg.setAttribute('height', `${SVG_BG_HEIGHT}`); // 减少SVG高度
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    
    // 添加半透明背景矩形
    const backgroundRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    backgroundRect.setAttribute('width', `${width}`);
    backgroundRect.setAttribute('height', '10'); // 中等刻度线高度8px + 2px = 10px
    backgroundRect.setAttribute('fill', 'var(--maths-kit-ruler-mark-bg, rgba(240, 240, 240, 0.8))');
    backgroundRect.setAttribute('rx', '2');
    backgroundRect.setAttribute('ry', '2');
    
    // 根据位置调整背景矩形的位置
    if (position === 'bottom') {
      backgroundRect.setAttribute('y', `${SVG_BG_HEIGHT - 10}`); // 28 - 10 = 18
    } else {
      backgroundRect.setAttribute('y', '0');
    }
    
    svg.appendChild(backgroundRect);
    
    // 创建刻度线和数值
    for (let i = 0; i <= totalMarks; i++) {
      const x = SVG_PADDING + (i * MARK_SPACING_PX);
      const markValue = i; // 刻度值（毫米）
      
      // 确定刻度线高度
      let markHeight = 4; // 默认小刻度高度
      let isMajorMark = false;
      let isMediumMark = false;
      
      // 每10mm（1cm）为大刻度
      if (markValue % MM_PER_CM === 0) {
        markHeight = 12;
        isMajorMark = true;
      } else if (markValue % 5 === 0) {
        // 每5mm为中等刻度
        markHeight = 8;
        isMediumMark = true;
      }
      
      // 创建刻度线
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const x1 = position === 'left' ? x-10 : position === 'bottom' ? x - 20 : x;
      const x2 = position === 'left' ? x-10 : position === 'bottom' ? x - 20 : x;
      const y1 = position === 'top' || position === 'left' ? '0' : `${SVG_BG_HEIGHT}`;
      const y2 = position === 'top' || position === 'left' ? `${markHeight}` : `${SVG_BG_HEIGHT - markHeight}`;
      line.setAttribute('x1', `${x1}`);
      line.setAttribute('y1', `${y1}`);
      line.setAttribute('x2', `${x2}`);
      line.setAttribute('y2', `${y2}`);
      line.setAttribute('stroke', isMajorMark ? 'var(--maths-kit-ruler-major-mark, #000)' : isMediumMark ? 'var(--maths-kit-ruler-medium-mark, #666)' : 'var(--maths-kit-ruler-minor-mark, #999)');
      line.setAttribute('stroke-width', `${MARK_WIDTH_PX}`);
      line.setAttribute('stroke-opacity', isMajorMark ? '1' : isMediumMark ? '0.8' : '0.6');
      svg.appendChild(line);
      
      // 为大刻度添加数值标签
      if (isMajorMark) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const textValue = Math.floor(markValue / MM_PER_CM); // 转换为厘米
        if (position === 'left' && textValue === 0) {
          text.textContent = '';
        } else {
          text.textContent = `${textValue}`;
        }
        const y = position === 'top' || position === 'left' ? '24' : '8';
        text.setAttribute('x', `${x1}`);
        text.setAttribute('y', `${y}`);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('font-size', '11px');
        text.setAttribute('fill', 'var(--maths-kit-ruler-text, #000)');
        text.setAttribute('font-family', 'Arial, sans-serif');
        // text.setAttribute('font-weight', 'bold');
        svg.appendChild(text);
      }
    }
    
    if (position === 'bottom') {
      // 添加单位标识
      const unitText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      unitText.textContent = 'cm';
      unitText.setAttribute('x', `${SVG_PADDING - 12}`);
      unitText.setAttribute('y', '8');
      unitText.setAttribute('font-size', '9px');
      unitText.setAttribute('fill', 'var(--maths-kit-ruler-unit-text, #666)');
      unitText.setAttribute('font-family', 'Arial, sans-serif');
      unitText.setAttribute('font-style', 'italic');
      svg.appendChild(unitText);
    }

    mark.appendChild(svg);
  }

  setActive(isActive: boolean) {
    this._isActive = isActive;
    if (isActive) {
      this.triangleView.style.pointerEvents = 'auto';
      if (this.angleInput) {
        this.angleInput.style.pointerEvents = 'auto';
      }
    } else {
      this.triangleView.style.pointerEvents = 'none';
      if (this.angleInput) {
        this.angleInput.style.pointerEvents = 'none';
      }
    }
  }

  private createDeleteBtn() {
    this.deleteBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.deleteBtn.style, {
      position: 'absolute',
      top: '40px',
      left: '15%',
      width: '20px',
      height: '20px',
      transform: 'translateY(-50%) scale(1)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
    });
    
    // 添加删除图标
    this.deleteBtn.textContent = '×';
    this.triangleView.appendChild(this.deleteBtn);
  }

  private createRotateBtn() {
    this.rotateBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.rotateBtn.style, {
      position: 'absolute',
      top: '40px',
      right: '35%',
      width: '20px',
      height: '20px',
      transform: 'translateY(-50%) scale(1)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
    });
    
    // 添加旋转图标
    this.rotateBtn.textContent = '↻';
    this.rotateBtn.setAttribute('data-type', 'Rotate');
    this.triangleView.appendChild(this.rotateBtn);
  }

  private createStretchBtn() {
    this.stretchBtn = document.createElement('div');
    this.stretchBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.stretchBtn.style, {
      position: 'absolute',
      top: '40px',
      left: '50%',
      width: '20px',
      height: '20px',
      transform: 'translateY(-50%) scale(1) rotate(-90deg)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
    });
    
    // 添加拉伸图标
    this.stretchBtn.textContent = '⇥';
    this.stretchBtn.setAttribute('data-type', 'Stretch');
    this.triangleView.appendChild(this.stretchBtn);
  }

  private createAngleInput() {
    this.angleInput.type = 'text';
    this.angleInput.classList.add('maths-kit-angle-input');
    
    // 应用内联样式
    Object.assign(this.angleInput.style, {
      position: 'absolute',
      top: '40px',
      left: '35%',
      transform: 'translate(-50%, -50%)',
      width: '60px',
      height: '24px',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '1px solid var(--maths-kit-math-kit-btn-border, #eee)',
      borderRadius: '4px',
      fontSize: '12px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      outline: 'none',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '1001',
    });
    
    // 设置初始值
    this.updateAngleDisplay(0);
    this.triangleView.appendChild(this.angleInput);
  }

  private onAngleInputFocusHandler = () => {
    this.angleInput.select();
  };

  private onAngleInputBlurHandler = () => {
    this.angleInput.style.opacity = '0';
    this.angleInput.style.pointerEvents = 'none';
    this.applyAngleFromInput();
  };

  private onAngleInputKeyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.angleInput.blur();
    } else if (e.key === 'Escape') {
      this.angleInput.value = this.getCurrentAngle().toString();
      this.angleInput.blur();
    }
  };

  private updateAngleDisplay = (angle: number) => {
    this.angleInput.value = `${angle.toFixed(1)}°`;
  };

  private getCurrentAngle(): number {
    // 从状态矩阵中提取旋转角度，而不是显示矩阵
    const stateMatrix = new Matrix2D(...this.state.matrix);
    const [a, b] = stateMatrix.toArray();
    let angle = Math.round(Math.atan2(b, a) * (180 / Math.PI) * 100) / 100;
    // 将角度转换为与 getAngleByPoints 一致的方向和范围
    angle = -angle; // 取负值以匹配 getAngleByPoints 的方向
    
    // 确保角度在 0-360 范围内
    if (angle < 0) {
      angle += 360;
    }
    return angle;
  }

  private applyAngleFromInput() {
    const value = this.angleInput.value.replace('°', '').trim();
    const targetAngle = parseFloat(value);
    
    if (!isNaN(targetAngle)) {
      // 获取当前角度
      const currentAngle = this.getCurrentAngle();
      
      // 计算需要旋转的角度差
      let angleDiff = targetAngle - currentAngle;
      
      // 确保角度差在合理范围内
      if (angleDiff > 180) {
        angleDiff -= 360;
      } else if (angleDiff < -180) {
        angleDiff += 360;
      }
      console.log('偏移计算:angleDiff', angleDiff);
      // 应用旋转角度差
      const angleInRadians = (angleDiff * Math.PI) / 180;
      const rotationMatrix = createRotationMatrix(angleInRadians);
      const currentMatrix = this.getDomMatrix();
      const newMatrix = composeTransforms(currentMatrix, rotationMatrix); 
      this.updateStateMatrix(newMatrix);
    } else {
      // 如果输入无效，恢复当前角度
      this.updateAngleDisplay(this.getCurrentAngle());
    }
  }

  // 添加鼠标悬停显示输入框的功能
  private showAngleInput = () => {
    this.hideLineLength();
    this.angleInput.style.opacity = '1';
    this.angleInput.style.pointerEvents = this._isActive ? 'auto' : 'none';
    this.updateAngleDisplay(this.getCurrentAngle());
  };

  private hideAngleInput = () => {
    if (document.activeElement !== this.angleInput) {
      this.angleInput.style.opacity = '0';
      this.angleInput.style.pointerEvents = 'none';
    }
  };

  showLineLength(number: number): void {
    this.hideAngleInput();
    this.lineLength.style.display = 'block';
    // 已知道number是px，需要转换为mm
    // 1mm = 96/25.4 像素
    const cm = number * MM_PER_PIXELS / 10;
    this.lineLength.textContent = `${cm.toFixed(1)}cm`;
  }

  hideLineLength(): void {
    this.lineLength.style.display = 'none';
  }

  private createLineLength() {
    this.lineLength = document.createElement('div');
    this.lineLength.classList.add('maths-kit-line-lenth');
    // 应用内联样式
    Object.assign(this.lineLength.style, {
      position: 'absolute',
      top: '40px',
      left: '35%',
      transform: 'translate(-50%, -50%)',
      width: '60px',
      height: '24px',
      lineHeight: '24px',
      fontSize: '12px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      outline: 'none',
      zIndex: '1001',
      pointerEvents: 'none',
    });
    this.triangleView.appendChild(this.lineLength);
  }
}

export class Triangle45Tool extends MathsKit {
  protected markLines: MarkLine[] = [];
  private deleteBtn: HTMLDivElement = document.createElement('div');
  private rotateBtn: HTMLDivElement = document.createElement('div');
  private angleInput: HTMLInputElement = document.createElement('input');
  private lineLength: HTMLDivElement = document.createElement('div');
  private stretchBtn!: HTMLDivElement;
  private triangleView!: HTMLDivElement;
  private elementContent!: HTMLDivElement;
  private bottomMark!: HTMLDivElement;
  private leftMark!: HTMLDivElement;
  private hypotenuseMark!: HTMLDivElement;

  bindElement(element: MathsKitElement, type: MathsKitType) {
    this.element.classList.add('maths-kit', `maths-kit-${type}`, `maths-kit-${this.manager.theme}`);
    this.manager.container.appendChild(element);
  }

  private bindElementContent(element: MathsKitElement){
    this.elementContent = document.createElement('div');
    this.elementContent.classList.add('maths-kit-content');
    element.appendChild(this.elementContent);
  }

  bindTriangleView(element: MathsKitElement){
    const triangleView = document.createElement('div');
    triangleView.classList.add('maths-kit-view');
    triangleView.setAttribute('data-type', 'Drag');
    triangleView.setAttribute('data-id', this.id);
    element.appendChild(triangleView);
    return triangleView;
  }
  
  constructor(options: MathsKitOptions, manager: MathsKitManager) {
    super(options, manager);
    this.bindElementContent(this.element);
  }

  init(): void {
    this.triangleView = this.bindTriangleView(this.elementContent);
    this.bottomMark = this.createMark(this.elementContent, 'bottom');
    this.leftMark = this.createMark(this.elementContent, 'left');
    this.hypotenuseMark = this.createMark(this.triangleView, 'top');
    this.createDeleteBtn();
    this.createRotateBtn();
    this.createStretchBtn();
    this.createAngleInput();
    this.createLineLength();
    this.bindEvent();
    this.render();
  }

  onDragStart(): void {
    this.toTop();
  }

  onDragDoing(): void {}

  onDragEnd(matrix: Matrix2D): void {
    this.updateStateMatrix(matrix);
  }

  onRotateStart(originAngle: number): void {
    this.toTop();
    this.updateAngleDisplay(originAngle);
  }

  onRotateDoing(angle: number): void {
    this.updateAngleDisplay(angle);
  }

  onRotateEnd(matrix: Matrix2D): void {
    this.updateStateMatrix(matrix);
  }

  onStretchStart(): void {
    this.toTop();
    this.thottled.cancel();
  }

  private thottled = throttle((_width: number): void => {
    this.element.style.width = `${_width}px`;
    this.elementContent.style.width = `${_width}px`;
    this.elementContent.style.height = `${this.computeHeight(_width)}px`;
    this.triangleView.style.width = `${this.computeHypotenuse(_width)}px`;
    this.renderMark(this.elementContent);
  },50);

  onStretchDoing = (_width: number): void => {
    this.thottled(_width);    
  };

  onStretchEnd(width: number): void {
    this.thottled.cancel();
    // 更新三角形的宽度
    this.manager.update(this.id, { width: width });
  }

  /**
   * 获取旋转中心点, 坐标必须相对于容器左上角, 旋转中心点是左上角
   * @returns 
   */
  getRotateCenterPoint = () => {
    const { x, y } = this.state;
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const pxX = x * containerWidth;
    const pxY = y * containerHeight;
    const domMatrix = this.getDomMatrix();
    const { translateX, translateY } = decomposeMatrix(domMatrix);
    const leftTopPoint = new Vec2d(pxX + translateX, pxY + translateY);
    // 返回 Point2d 对象
    return new Point2d(leftTopPoint.x, leftTopPoint.y);
  };

  getMarkLines(): MarkLine[] {
    const { x, y, width } = this.state;
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const { translateX, translateY, scaleX, scaleY } = decomposeMatrix(this.getDomMatrix());
    const height = this.computeHeight(width);
    // 45-45-90三角形的三个顶点
    const baseWidth = width * scaleX;
    const baseHeight = height * scaleY;

    const pxX = x * containerWidth;
    const pxY = y * containerHeight;
    
    // 计算三角形的三个顶点（45-45-90三角形）
    // 直角在左下角 (0, height)
    // 底边（x轴邻边）从 (0, height) 到 (width, height)
    // 左边（y轴邻边）从 (0, height) 到 (0, 0)
    // 斜边从 (0, 0) 到 (width, height)
    const leftPoint0 = new Vec2d(pxX , pxY - baseHeight );
    const leftPoint1 = new Vec2d(pxX, pxY);

    const hypotenusePoint0 = new Vec2d(pxX, pxY - baseHeight);
    const hypotenusePoint1 = new Vec2d(pxX + baseWidth, pxY);

    const bottomPoint0 = new Vec2d(pxX, pxY);
    const bottomPoint1 = new Vec2d(pxX + baseWidth, pxY);
    
    // 应用变换
    leftPoint0.add(new Vec2d(translateX, translateY));
    leftPoint1.add(new Vec2d(translateX, translateY));
    hypotenusePoint0.add(new Vec2d(translateX, translateY));
    hypotenusePoint1.add(new Vec2d(translateX, translateY));
    bottomPoint0.add(new Vec2d(translateX, translateY));
    bottomPoint1.add(new Vec2d(translateX, translateY));
    
    const currentAngle = this.getCurrentAngle();
    const angleInRadians = (currentAngle * Math.PI) / 180;
    const rotateCenterPoint = this.getRotateCenterPoint();
    
    leftPoint0.rotWith(rotateCenterPoint, -angleInRadians);
    leftPoint1.rotWith(rotateCenterPoint, -angleInRadians);
    hypotenusePoint0.rotWith(rotateCenterPoint, -angleInRadians);
    hypotenusePoint1.rotWith(rotateCenterPoint, -angleInRadians);
    bottomPoint0.rotWith(rotateCenterPoint, -angleInRadians);
    bottomPoint1.rotWith(rotateCenterPoint, -angleInRadians);

    const leftLine = new LineSegment(leftPoint0, leftPoint1);
    const hypotenuse = new LineSegment(hypotenusePoint0, hypotenusePoint1);
    const baseLine = new LineSegment(bottomPoint0, bottomPoint1);

    const markLines: MarkLine[] = [
      {
        axis: 'y',
        direction: 'left',
        line: leftLine,
      },
      {
        axis: 'x',
        direction: 'topLeftToBottomRight',
        line: hypotenuse,
      },
      {
        axis: 'x',
        direction: 'bottom',
        line: baseLine,
      },
    ];
    return markLines;
  }

  private bindEvent() {
    this.deleteBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.deleteBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    this.deleteBtn.addEventListener('click', this.onDeleteBtnClickHandler);

    this.stretchBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.stretchBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    
    this.rotateBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.rotateBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    
    this.angleInput.addEventListener('focus', this.onAngleInputFocusHandler);
    this.angleInput.addEventListener('blur', this.onAngleInputBlurHandler);
    this.angleInput.addEventListener('keydown', this.onAngleInputKeyDownHandler);
    
    // 添加鼠标悬停显示角度输入框
    this.rotateBtn.addEventListener('mouseenter', this.showAngleInput);
    this.angleInput.addEventListener('mouseenter', this.showAngleInput);
  }

  private unbindEvent() {
    if (this.deleteBtn) {
      this.deleteBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.deleteBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
      this.deleteBtn.removeEventListener('click', this.onDeleteBtnClickHandler);
    }

    if (this.stretchBtn) {
      this.stretchBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.stretchBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
    }
    
    if (this.rotateBtn) {
      this.rotateBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.rotateBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
    }
    
    if (this.angleInput) {
      this.angleInput.removeEventListener('focus', this.onAngleInputFocusHandler);
      this.angleInput.removeEventListener('blur', this.onAngleInputBlurHandler);
      this.angleInput.removeEventListener('keydown', this.onAngleInputKeyDownHandler);
      // 移除鼠标悬停事件
      this.rotateBtn.removeEventListener('pointerenter', this.showAngleInput);
      this.angleInput.removeEventListener('pointerenter', this.showAngleInput);
    }
    this.thottled.cancel();
  }

  destroy(): void {
    this.triangleView.remove();
    this.bottomMark.remove();
    this.leftMark.remove();
    this.hypotenuseMark.remove();
    super.destroy();
    this.unbindEvent();
  }

  private onDeleteBtnClickHandler = (e: MouseEvent) => {
    e.stopPropagation();
    this.manager.remove(this.id);
  };

  private onBtnMouseEnterHandler = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.background = 'var(--maths-kit-math-kit-btn-hover-bg, #f0f4ff)';
    if (e.target === this.stretchBtn) {
      target.style.transform = 'translateY(-50%) scale(1.1) rotate(-90deg)';
    } else if (e.target === this.deleteBtn) {
      target.style.transform = 'translateY(-50%) scale(1.1) rotate(45deg)';
    }   else {
      target.style.transform = 'translateY(-50%) scale(1.1)';
    }
  };

  private onBtnMouseLeaveHandler = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.background = 'var(--maths-kit-math-kit-btn-bg, #fff)';
    if (e.target === this.stretchBtn) {
      target.style.transform = 'translateY(-50%) scale(1) rotate(-90deg)';
    } else if (e.target === this.deleteBtn) {
      target.style.transform = 'translateY(-50%) scale(1) rotate(45deg)';
    } else {
      target.style.transform = 'translateY(-50%) scale(1)';
    }
  };

  createElement(): HTMLDivElement {
    const element = document.createElement('div');
    
    // 确保容器有正确的定位上下文
    element.style.position = 'relative';
    element.style.transformOrigin = 'left bottom';
    element.style.touchAction = 'none';

    return element;
  }

  private createMark(element: HTMLDivElement, position: 'bottom' | 'left' | 'top') {
    const mark = document.createElement('div');
    mark.classList.add('maths-kit-triangle-mark', position);
    // 应用内联样式
    Object.assign(mark.style, {
      position: 'absolute',
      left: position === 'left' ? '28px' : '0',
      background: 'transparent',
      ...(position === 'top' ? { 
        top: '0',
        right: '0',
        borderBottom: 'none',
      } : position === 'left' ? { 
        bottom: '0',
        borderRight: 'none',
        transform: 'rotate(-90deg)',
        transformOrigin: 'left bottom',
      } : { 
        bottom: '0',
        right: '0',
        borderTop: 'none',
      }),
      pointerEvents: 'none',
    });
    element.appendChild(mark);
    return mark;
  }

  private computeHeight(width: number) {
    return width;
  }

  private computeHypotenuse(width: number) {
    return width * Math.sqrt(2);
  }

  render(): void {
    const state = this.state;
    const domMatrix = this.getDomMatrix();
    const height = this.computeHeight(state.width);
    const width = state.width;

    const style = {
      position: 'absolute',
      left: `${state.x * 100}%`,
      top: `${state.y * 100}%`,
      height: '1px',
      width: `${width}px`,
      minWidth: '200px',
      transform: domMatrix.toCSSTransform(),
      zIndex: state.zIndex,
      boxSizing: 'content-box',
    };
    Object.assign(this.element.style, style);

    const elementContentStyle = {
      position: 'absolute',
      bottom: '0',
      left: '0',
      width: `${width}px`,
      height: `${height}px`,
      minWidth: '200px',
      minHeight: `${this.computeHeight(200)}px`,
      overflow: 'hidden',
      boxSizing: 'content-box',
      borderLeft: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      borderBottom: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      borderRight: '0',
      borderTop: '0',
      pointerEvents: 'none',
    };
    Object.assign(this.elementContent.style, elementContentStyle);

    const angleInRadians = (315 * Math.PI) / 180;
    const triangleMatrix = createRotationMatrix(angleInRadians);
    const hypotenuse = this.computeHypotenuse(state.width);
    const hypotenuseHeight = this.computeHeight(hypotenuse);
    const minHypotenuseWidth = this.computeHypotenuse(200);
    const minHypotenuseHeight = this.computeHeight(hypotenuse);
    const triangleViewStyle = {
      position: 'absolute',
      left: '0',
      top: '0',
      width: `${hypotenuse}px`,
      height: `${hypotenuseHeight}px`,
      minWidth: `${minHypotenuseWidth}px`,
      minHeight: `${minHypotenuseHeight}px`,
      transform: triangleMatrix.toCSSTransform(),
      boxSizing: 'border-box',
      transformOrigin: 'left top',
      borderTop: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      backgroundColor: 'var(--maths-kit-ruler-bg, rgba(255, 255, 255, 0.8))',
      // border: '1px solid var(--maths-kit-ruler-border, rgba(225, 225, 225, 0.8))',
      pointerEvents: this._isActive ? 'auto' : 'none',
      cursor: 'pointer',
    };
    Object.assign(this.triangleView.style, triangleViewStyle);
    
    // 使用setTimeout确保DOM完全渲染后再获取尺寸和重新绑定事件
    setTimeout(() => {
      this.renderMark(this.elementContent);
    }, 0);

  }
  private renderMark(element: Triangle45Element) {
    const marks = element.querySelectorAll('.maths-kit-triangle-mark') as NodeListOf<HTMLDivElement>;
    marks.forEach((mark) => {
      this.renderMarkAtPosition(mark);
    });
  }

  private renderMarkAtPosition(mark: HTMLDivElement) {
    const position = mark.classList.contains('top') ? 'top' : mark.classList.contains('bottom') ? 'bottom' : 'left';
    // 清空现有内容
    mark.innerHTML = '';

    let svgWidth = 0;
    // SVG内部padding
    if(position === 'left' || position === 'bottom') {
      const width = this.elementContent.offsetWidth;
      if (width <= 0) return;
      svgWidth = width;
    } else {
      const width = mark.offsetWidth;
      if (width <= 0) return;
      svgWidth = width;
    }
    let SVG_PADDING = 40;
    if (position === 'top') {
      SVG_PADDING = 55;
    }

    const SVG_BG_HEIGHT = 28;

    // 保持原有的绝对定位，只更新必要的样式
    if (position === 'left') {
      mark.style.width = `${svgWidth}px`; // 减少高度，因为刻度线变短了
      mark.style.height = `${SVG_BG_HEIGHT}px`;
    } else {
      mark.style.height = `${SVG_BG_HEIGHT}px`; // 减少高度，因为刻度线变短了
    }
    mark.style.overflow = 'hidden';
    mark.style.zIndex = '1';

    // 获取设备像素比，用于更精确的物理尺寸计算
    // const devicePixelRatio = window.devicePixelRatio || 1;
    const MM_PER_CM = 10;
    
    // 刻度间距：1mm
    const MARK_SPACING_MM = 1;
    const MARK_SPACING_PX = MARK_SPACING_MM * PIXELS_PER_MM;
    
    // 刻度线宽度：1px
    const MARK_WIDTH_PX = 1;
    
    // 计算可以容纳的刻度数量（考虑SVG内部padding）
    const availableWidth = svgWidth - (SVG_PADDING * 2);
    const totalMarks = Math.floor(availableWidth / MARK_SPACING_PX);
    
    // 创建SVG容器
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const width = position === 'left' || position === 'bottom' ? svgWidth - 10 : svgWidth;
    svg.setAttribute('width', `${width}`);
    svg.setAttribute('height', `${SVG_BG_HEIGHT}`); // 减少SVG高度
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    
    // 添加半透明背景矩形
    const backgroundRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    backgroundRect.setAttribute('width', `${width}`);
    backgroundRect.setAttribute('height', '10'); // 中等刻度线高度8px + 2px = 10px
    backgroundRect.setAttribute('fill', 'var(--maths-kit-ruler-mark-bg, rgba(240, 240, 240, 0.8))');
    backgroundRect.setAttribute('rx', '2');
    backgroundRect.setAttribute('ry', '2');
    
    // 根据位置调整背景矩形的位置
    if (position === 'bottom') {
      backgroundRect.setAttribute('y', `${SVG_BG_HEIGHT - 10}`); // 28 - 10 = 18
    } else {
      backgroundRect.setAttribute('y', '0');
    }
    
    svg.appendChild(backgroundRect);
    
    // 创建刻度线和数值
    for (let i = 0; i <= totalMarks; i++) {
      const x = SVG_PADDING + (i * MARK_SPACING_PX);
      const markValue = i; // 刻度值（毫米）
      
      // 确定刻度线高度
      let markHeight = 4; // 默认小刻度高度
      let isMajorMark = false;
      let isMediumMark = false;
      
      // 每10mm（1cm）为大刻度
      if (markValue % MM_PER_CM === 0) {
        markHeight = 12;
        isMajorMark = true;
      } else if (markValue % 5 === 0) {
        // 每5mm为中等刻度
        markHeight = 8;
        isMediumMark = true;
      }
      
      // 创建刻度线
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const x1 = position === 'left' ||  position === 'bottom' ? x-10 : x;
      const x2 = position === 'left' || position === 'bottom' ? x-10 : x;
      const y1 = position === 'top' || position === 'left' ? '0' : `${SVG_BG_HEIGHT}`;
      const y2 = position === 'top' || position === 'left' ? `${markHeight}` : `${SVG_BG_HEIGHT - markHeight}`;
      line.setAttribute('x1', `${x1}`);
      line.setAttribute('y1', `${y1}`);
      line.setAttribute('x2', `${x2}`);
      line.setAttribute('y2', `${y2}`);
      line.setAttribute('stroke', isMajorMark ? 'var(--maths-kit-ruler-major-mark, #000)' : isMediumMark ? 'var(--maths-kit-ruler-medium-mark, #666)' : 'var(--maths-kit-ruler-minor-mark, #999)');
      line.setAttribute('stroke-width', `${MARK_WIDTH_PX}`);
      line.setAttribute('stroke-opacity', isMajorMark ? '1' : isMediumMark ? '0.8' : '0.6');
      svg.appendChild(line);
      
      // 为大刻度添加数值标签
      if (isMajorMark) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const textValue = Math.floor(markValue / MM_PER_CM); // 转换为厘米
        if (position === 'left' && textValue === 0) {
          text.textContent = '';
        } else {
          text.textContent = `${textValue}`;
        }
        const y = position === 'top' || position === 'left' ? '24' : '8';
        text.setAttribute('x', `${x1}`);
        text.setAttribute('y', `${y}`);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('font-size', '11px');
        text.setAttribute('fill', 'var(--maths-kit-ruler-text, #000)');
        text.setAttribute('font-family', 'Arial, sans-serif');
        // text.setAttribute('font-weight', 'bold');
        svg.appendChild(text);
      }
    }
    
    if (position === 'bottom') {
      // 添加单位标识
      const unitText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      unitText.textContent = 'cm';
      unitText.setAttribute('x', `${SVG_PADDING}`);
      unitText.setAttribute('y', '8');
      unitText.setAttribute('font-size', '9px');
      unitText.setAttribute('fill', 'var(--maths-kit-ruler-unit-text, #666)');
      unitText.setAttribute('font-family', 'Arial, sans-serif');
      unitText.setAttribute('font-style', 'italic');
      svg.appendChild(unitText);
    }

    mark.appendChild(svg);
  }

  setActive(isActive: boolean) {
    this._isActive = isActive;
    if (isActive) {
      this.triangleView.style.pointerEvents = 'auto';
      if (this.angleInput) {
        this.angleInput.style.pointerEvents = 'auto';
      }
    } else {
      this.triangleView.style.pointerEvents = 'none';
      if (this.angleInput) {
        this.angleInput.style.pointerEvents = 'none';
      }
    }
  }

  private createDeleteBtn() {
    this.deleteBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.deleteBtn.style, {
      position: 'absolute',
      top: '40px',
      left: '20%',
      width: '20px',
      height: '20px',
      transform: 'translateY(-50%) scale(1) rotate(45deg)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
    });
    
    // 添加删除图标
    this.deleteBtn.textContent = '×';
    this.triangleView.appendChild(this.deleteBtn);
  }

  private createRotateBtn() {
    this.rotateBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.rotateBtn.style, {
      position: 'absolute',
      top: '40px',
      right: '25%',
      width: '20px',
      height: '20px',
      transform: 'translateY(-50%) scale(1)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
    });
    
    // 添加旋转图标
    this.rotateBtn.textContent = '↻';
    this.rotateBtn.setAttribute('data-type', 'Rotate');
    this.triangleView.appendChild(this.rotateBtn);
  }

  private createStretchBtn() {
    this.stretchBtn = document.createElement('div');
    this.stretchBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.stretchBtn.style, {
      position: 'absolute',
      top: '40px',
      left: '55%',
      width: '20px',
      height: '20px',
      transform: 'translateY(-50%) scale(1) rotate(-90deg)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
    });
    
    // 添加拉伸图标
    this.stretchBtn.textContent = '⇥';
    this.stretchBtn.setAttribute('data-type', 'Stretch');
    this.triangleView.appendChild(this.stretchBtn);
  }

  private createAngleInput() {
    this.angleInput.type = 'text';
    this.angleInput.classList.add('maths-kit-angle-input');
    
    // 应用内联样式
    Object.assign(this.angleInput.style, {
      position: 'absolute',
      top: '40px',
      left: '40%',
      transform: 'translate(-50%, -50%)',
      width: '60px',
      height: '24px',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '1px solid var(--maths-kit-math-kit-btn-border, #eee)',
      borderRadius: '4px',
      fontSize: '12px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      outline: 'none',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '1001',
    });
    
    // 设置初始值
    this.updateAngleDisplay(0);
    this.triangleView.appendChild(this.angleInput);
  }

  private onAngleInputFocusHandler = () => {
    this.angleInput.select();
  };

  private onAngleInputBlurHandler = () => {
    this.angleInput.style.opacity = '0';
    this.angleInput.style.pointerEvents = 'none';
    this.applyAngleFromInput();
  };

  private onAngleInputKeyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.angleInput.blur();
    } else if (e.key === 'Escape') {
      this.angleInput.value = this.getCurrentAngle().toString();
      this.angleInput.blur();
    }
  };

  private updateAngleDisplay = (angle: number) => {
    this.angleInput.value = `${angle.toFixed(1)}°`;
  };

  private getCurrentAngle(): number {
    // 从状态矩阵中提取旋转角度，而不是显示矩阵
    const stateMatrix = new Matrix2D(...this.state.matrix);
    const [a, b] = stateMatrix.toArray();
    let angle = Math.round(Math.atan2(b, a) * (180 / Math.PI) * 100) / 100;
    // 将角度转换为与 getAngleByPoints 一致的方向和范围
    angle = -angle; // 取负值以匹配 getAngleByPoints 的方向
    
    // 确保角度在 0-360 范围内
    if (angle < 0) {
      angle += 360;
    }
    return angle;
  }

  private applyAngleFromInput() {
    const value = this.angleInput.value.replace('°', '').trim();
    const targetAngle = parseFloat(value);
    
    if (!isNaN(targetAngle)) {
      // 获取当前角度
      const currentAngle = this.getCurrentAngle();
      
      // 计算需要旋转的角度差
      let angleDiff = targetAngle - currentAngle;
      
      // 确保角度差在合理范围内
      if (angleDiff > 180) {
        angleDiff -= 360;
      } else if (angleDiff < -180) {
        angleDiff += 360;
      }
      console.log('偏移计算:angleDiff', angleDiff);
      // 应用旋转角度差
      const angleInRadians = (angleDiff * Math.PI) / 180;
      const rotationMatrix = createRotationMatrix(angleInRadians);
      const currentMatrix = this.getDomMatrix();
      const newMatrix = composeTransforms(currentMatrix, rotationMatrix); 
      this.updateStateMatrix(newMatrix);
    } else {
      // 如果输入无效，恢复当前角度
      this.updateAngleDisplay(this.getCurrentAngle());
    }
  }

  // 添加鼠标悬停显示输入框的功能
  private showAngleInput = () => {
    this.hideLineLength();
    this.angleInput.style.opacity = '1';
    this.angleInput.style.pointerEvents = this._isActive ? 'auto' : 'none';
    this.updateAngleDisplay(this.getCurrentAngle());
  };

  private hideAngleInput = () => {
    if (document.activeElement !== this.angleInput) {
      this.angleInput.style.opacity = '0';
      this.angleInput.style.pointerEvents = 'none';
    }
  };

  showLineLength(number: number): void {
    this.hideAngleInput();
    this.lineLength.style.display = 'block';
    // 已知道number是px，需要转换为mm
    // 1mm = 96/25.4 像素
    const cm = number * MM_PER_PIXELS / 10;
    this.lineLength.textContent = `${cm.toFixed(1)}cm`;
  }

  hideLineLength(): void {
    this.lineLength.style.display = 'none';
  }

  private createLineLength() {
    this.lineLength = document.createElement('div');
    this.lineLength.classList.add('maths-kit-line-lenth');
    // 应用内联样式
    Object.assign(this.lineLength.style, {
      position: 'absolute',
      top: '40px',
      left: '40%',
      transform: 'translate(-50%, -50%)',
      width: '60px',
      height: '24px',
      lineHeight: '24px',
      fontSize: '12px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      outline: 'none',
      zIndex: '1001',
      pointerEvents: 'none',
    });
    this.triangleView.appendChild(this.lineLength);
  }
}